import { ref } from "vue";
import api from "@/api/axios";
import { useOnlineStore } from "@/stores/onlineStore";

interface OnlineUser {
    id: string;
    name: string;
    email: string;
    role: string;
    last_activity_at?: number | null;
    online?: boolean;
}

interface ActivityPayload {
    userId: string;
    lastActivity: number;
}

let echoInstance: any = null;

export function usePresenceChannel() {
    const onlineStore = useOnlineStore();
    const isConnected = ref(false);
    const isLoading = ref(false);

    const connect = async () => {
        if (echoInstance) {
            return;
        }

        isLoading.value = true;

        try {
            const { default: Echo } = await import("laravel-echo");
            const { default: Pusher } = await import("pusher-js");

            window.Pusher = Pusher;

            echoInstance = new Echo({
                broadcaster: "reverb",
                key: import.meta.env.VITE_REVERB_APP_KEY,
                wsHost: import.meta.env.VITE_REVERB_HOST,
                wsPort: import.meta.env.VITE_REVERB_PORT ?? 8080,
                wssPort: import.meta.env.VITE_REVERB_PORT ?? 443,
                forceTLS:
                    (import.meta.env.VITE_REVERB_SCHEME ?? "https") === "https",
                enabledTransports: ["ws", "wss"],
                authorizer: (channel: any) => ({
                    authorize: (socketId: string, callback: Function) => {
                        api.post("/broadcasting/auth", {
                            socket_id: socketId,
                            channel_name: channel.name,
                        })
                            .then((response) => {
                                callback(null, response.data);
                            })
                            .catch((error) => {
                                callback(error, null);
                            });
                    },
                }),
            });

            echoInstance
                .join("online")
                .here((currentUsers: OnlineUser[]) => {
                    onlineStore.setUsers(currentUsers);
                    isConnected.value = true;
                    isLoading.value = false;
                })
                .joining((user: OnlineUser) => {
                    onlineStore.addUser(user);
                })
                .leaving((user: OnlineUser) => {
                    onlineStore.removeUser(user.id);
                });

            echoInstance
                .private("online-activity")
                .listen("UserActivityUpdated", (e: ActivityPayload) => {
                    onlineStore.updateActivity(e.userId, e.lastActivity);
                });

            window.Echo = echoInstance;
        } catch (error) {
            console.error("Failed to initialize Echo:", error);
            isLoading.value = false;
        }
    };

    const disconnect = () => {
        if (echoInstance) {
            echoInstance.leave("online");
            echoInstance.disconnect();
            echoInstance = null;
            window.Echo = null;
            onlineStore.clearUsers();
            isConnected.value = false;
        }
    };

    return {
        isConnected,
        isLoading,
        connect,
        disconnect,
    };
}
